import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import {
  ClientDashboardMetadata,
  ClientLayout,
  ClientLayoutItemType,
  LoadObjectRequest,
  RelationSearchResult,
} from '@mmlogic/components';
// ClientLayoutItem intentionally not imported — form-domain logic lives in mrd-form
import { DataObject } from 'src/app/core/model/data-model/data-object.model';
import { ApiClientService } from 'src/app/core/services/api-client.service';
import { AppService } from 'src/app/core/services/app.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-object-form',
  templateUrl: './object-form.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ObjectFormComponent implements OnInit {
  tenantCode: string;
  referenceClass: string;
  referenceId: string;
  type: string;
  objectClass: string | null;
  referenceMode: boolean;
  newClass: string | null;

  dataObject: DataObject | undefined;
  id: string;

  metadata: ClientDashboardMetadata;
  clientLayout: ClientLayout | null = null;
  formValues: Record<string, unknown> = {};

  /** Href of the object to load directly, bypassing ActivatedRoute — set when this
   *  form instance is rendered inside DashboardComponent's overlay shell (same
   *  convention as DashboardComponent's own overlayHref). Only the id-based
   *  edit-existing-object flow is supported in this mode. */
  @Input() overlayHref: string | null = null;
  /** Opt-in: shows mrd-form's delete button (edit mode only). Off by default —
   *  standard object forms don't get a delete affordance here; only routes that
   *  explicitly want it (currently none — memo has its own dialog-level delete,
   *  unrelated to this form) should set it. */
  @Input() showDelete = false;
  @Output() overlaySaved = new EventEmitter<void>();
  @Output() overlayCancelled = new EventEmitter<void>();
  /** The edited record was deleted from within the form — distinct from
   *  overlaySaved/overlayCancelled since the record no longer exists to refresh. */
  @Output() overlayDeleted = new EventEmitter<void>();

  deleteConfirmOpen = false;

  @ViewChild('mrdFormEl') mrdFormEl: ElementRef;

  constructor(
    private api: ApiClientService,
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private notificationService: NotificationService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    if (this.overlayHref) {
      this.refresh();
      return;
    }
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: Params) => this.refresh(params));
  }

  refresh(_params?: Params) {
    if (this.overlayHref) {
      const [, , tenantCode, type, id] = this.overlayHref.split('/');
      this.tenantCode = tenantCode;
      this.type = type;
      this.id = id;
      this.referenceClass = undefined as any;
      this.referenceId = undefined as any;
      this.referenceMode = false;
      this.objectClass = null;
      this.newClass = null;
      this.clientLayout = null;
      this.formValues = {};
      this.setMetadata(this.loadDataObject());
      return;
    }

    this.tenantCode = this.route.snapshot.params['tenantCode'];
    this.referenceClass = this.route.snapshot.params['referenceClass'];
    this.referenceId = this.route.snapshot.params['referenceId'];
    this.type = this.route.snapshot.params['type'];
    this.objectClass = this.route.snapshot.queryParams['objectClass'] ?? null;
    this.referenceMode = !!this.referenceClass && !!this.referenceId;
    this.id = this.route.snapshot.params['id'];
    this.newClass = this.route.snapshot.queryParams['newClass'];

    this.clientLayout = null;
    this.formValues = {};

    if (this.id) {
      this.setMetadata(this.loadDataObject());
      return;
    }

    if (this.referenceMode) {
      const metadata$ = this.isSystemRoute
        ? this.api.getSystemRelatedObjectsForm(this.tenantCode, this.referenceClass, this.type, this.objectClass)
        : this.api.getRelatedObjectsForm(this.tenantCode, this.referenceClass, this.type, this.objectClass);
      const parent$ = this.isSystemRoute
        ? this.api.getSystemObject(this.tenantCode, this.referenceClass, this.referenceId)
        : this.api.getObject(this.tenantCode, this.referenceClass, this.referenceId);
      forkJoin({ metadata: metadata$, parent: parent$ }).subscribe(({ metadata, parent }) => {
        this.applyMetadata(metadata);
        const fieldName = this.findParentRelationField(this.clientLayout!.items, this.referenceClass);
        if (fieldName) {
          this.formValues = { [fieldName]: { id: parent._links._self.href, label: parent.name } };
        }
      });
      return;
    }

    this.setMetadata(
      this.isSystemRoute
        ? this.api.getSystemClassCreateForm(this.tenantCode, this.type, this.objectClass)
        : this.api.getClassCreateForm(this.tenantCode, this.type, this.objectClass, null)
    );
  }

  setMetadata(observer: Observable<ClientDashboardMetadata>) {
    observer.subscribe((metadata) => this.applyMetadata(metadata));
  }

  private applyMetadata(metadata: ClientDashboardMetadata) {
    this.metadata = metadata;
    // Merge all layouts into one ClientLayout, wrapping tab pages in SECTION items
    const allItems = metadata.layouts.flatMap((layout) => {
      if (layout.tabPage && layout.label) {
        return [{ type: ClientLayoutItemType.SECTION, label: layout.label, items: layout.items }];
      }
      return layout.items;
    });
    this.clientLayout = { items: allItems };
  }

  private findParentRelationField(items: any[], referenceClass: string): string | null {
    for (const item of items) {
      if (item.type === ClientLayoutItemType.RELATION && item.relation?.relatedClass === referenceClass) {
        return item.relation.name;
      }
      if (item.items) {
        const found = this.findParentRelationField(item.items, referenceClass);
        if (found) return found;
      }
    }
    return null;
  }

  get locale(): string {
    return this.appService.getLocale();
  }

  get me(): { href: string; name: string } | null {
    return this.appService.userProfileSubject.getValue()?._links?.person ?? null;
  }

  /** Absolute href of the reference/parent object, passed to mrd-form so it can
   *  pre-fill dependent DROPDOWN fields without any host-side form logic. */
  get isSystemRoute(): boolean {
    if (this.overlayHref) {
      return this.overlayHref.startsWith('/system/');
    }
    return !!this.route.snapshot.data['system'];
  }

  get referenceHref(): string {
    if (!this.referenceMode) return '';
    return `${environment.endpoints.data}/${this.tenantCode}/${this.referenceClass}/${this.referenceId}`;
  }

  loadDataObject(): Observable<ClientDashboardMetadata> {
    const object$ = this.isSystemRoute
      ? this.api.getObjectByUrl(`/system/${this.tenantCode}/${this.type}/${this.id}`)
      : this.api.getObject(this.tenantCode, this.type, this.id);
    return object$.pipe(
      mergeMap((dataObject) => {
        this.dataObject = dataObject;
        const { _links, ...fields } = dataObject;
        this.formValues = { ...fields, ...(_links ?? {}) };

        const options = this.newClass ? '&newClass=' + this.newClass : '';
        return this.api.getDashboardByUrl(dataObject._links._form.href + options);
      }),
    );
  }

  /** A 4xx on a form's create/patch (validation, a unique-constraint conflict, a
   *  rejected file claim) is something the user caused and can act on — surface
   *  it instead of letting it fall through to GlobalErrorHandler's console-only
   *  logging. 5xx/network failures aren't the user's to fix from here, so they're
   *  left to that same fallback. */
  private handleSubmitError = (error: unknown): void => {
    if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
      this.notificationService.showServerError(error, 'save.failed', 'WARNING');
    }
  };

  onMrdSubmit(event: CustomEvent<Record<string, unknown>>) {
    const data = event.detail;

    if (this.id) {
      const realType = this.newClass ?? this.type;
      if (this.isSystemRoute) {
        this.api
          .updateObjectByUrl(this.dataObject!._links._self.href, data)
          .subscribe({
            next: () => {
              if (this.overlayHref) { this.overlaySaved.emit(); return; }
              this.router.navigate(['system', this.tenantCode, realType, this.id]);
            },
            error: this.handleSubmitError,
          });
      } else {
        this.api
          .updateObject(this.tenantCode, realType, this.id, data)
          .subscribe({
            next: () => {
              if (this.overlayHref) { this.overlaySaved.emit(); return; }
              this.router.navigate(['data', this.tenantCode, realType, this.id]);
            },
            error: this.handleSubmitError,
          });
      }
    } else if (this.referenceMode && this.isSystemRoute) {
      this.api
        .createReferencedSystemObject(this.tenantCode, this.referenceClass, this.referenceId, this.type, data)
        .subscribe({
          next: () => this.router.navigate(['../..'], { relativeTo: this.route }),
          error: this.handleSubmitError,
        });
    } else if (this.referenceMode) {
      this.api
        .createReferencedObject(
          this.tenantCode,
          this.referenceClass,
          this.referenceId,
          this.type,
          this.objectClass,
          data,
        )
        .subscribe({
          next: () => this.router.navigate(['../..'], { relativeTo: this.route }),
          error: this.handleSubmitError,
        });
    } else if (this.isSystemRoute) {
      this.api
        .createSystemObject(this.tenantCode, this.type, data)
        .subscribe({
          next: (url) => this.router.navigate([url]),
          error: this.handleSubmitError,
        });
    } else {
      this.api
        .createObject(this.tenantCode, this.type, data)
        .subscribe({
          next: (url) => this.router.navigate([url]),
          error: this.handleSubmitError,
        });
    }
  }

  onMrdSearch(event: CustomEvent<{ name: string; query: string; relatedClass: string }>) {
    const { name, query, relatedClass } = event.detail;

    this.api
      .getObjectsByUrl(this.api.getSearchUrl(this.tenantCode, relatedClass, query))
      .subscribe((result) => {
        const embedded = result._embedded ? Object.values(result._embedded)[0] : [];
        const objects = Array.isArray(embedded) ? (embedded as any[]) : [];
        const results: RelationSearchResult[] = objects.map((o) => ({
          id: o._links?._self?.href ?? '',
          label: o.name ?? '',
        }));

        if (!this.mrdFormEl) return;
        const fields =
          this.mrdFormEl.nativeElement.querySelectorAll('mrd-relation-field');
        const target = Array.from(fields).find((f: any) => f.name === name) as any;
        target?.setSearchResults(results);
      });
  }

  /** Answers a RELATED_OBJECT block's object/layout request. Re-emitted whenever the
   *  relation changes; each event is answered independently on the element that emitted
   *  it (the form itself, or a section nested in a related layout). */
  onMrdLoadObject(event: CustomEvent<LoadObjectRequest>) {
    const target = event.target as any;
    const { name } = event.detail;
    this.api.loadObject(event.detail)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ data, layout }) => target?.setObject(name, data, layout));
  }

  onMrdFetchAll(event: CustomEvent<{
    name: string;
    relatedClass: string;
    mostSignificantClass: string;
    commonRelation?: string;
    filter?: string;
    filterValue?: string;
  }>) {
    const { name, mostSignificantClass, filter, filterValue } = event.detail;

    if (!this.mrdFormEl) return;
    const fields = this.mrdFormEl.nativeElement.querySelectorAll('mrd-relation-field');
    const target = Array.from(fields).find((f: any) => f.name === name) as any;
    if (!target) return;

    // Dependency was cleared → empty the dropdown immediately without an API call.
    if (filter && !filterValue) {
      target.setAllRecords([]);
      return;
    }

    let url = `/data/${this.tenantCode}/${mostSignificantClass}`;
    if (filter && filterValue) {
      url += `?${encodeURIComponent(filter)}=${encodeURIComponent(filterValue)}`;
    }

    this.api.getObjectsByUrl(url).subscribe((result) => {
      const embedded = result._embedded ? Object.values(result._embedded)[0] : [];
      const objects = Array.isArray(embedded) ? (embedded as any[]) : [];
      const records: RelationSearchResult[] = objects.map((o) => ({
        id: o._links?._self?.href ?? '',
        label: o.name ?? '',
      }));
      target.setAllRecords(records);
    });
  }

  onMrdUpload(event: CustomEvent<{ name: string; file: File }>) {
    const { name, file } = event.detail;

    this.api.upload(this.tenantCode, file).subscribe((uploaded) => {
      if (!this.mrdFormEl) return;
      // Echo the server's own fileName/contentType/size back untouched — claimBinary
      // on save verifies it against what it recorded at upload time (TASK-0351).
      // Passing `file` back also lets mrd-form resolve the right entry for `multiple`
      // fields (concurrent uploads) without disturbing other pending files.
      this.mrdFormEl.nativeElement.setFieldValue(name, uploaded, file);
    });
  }

  onCancel() {
    if (this.overlayHref) {
      this.overlayCancelled.emit();
      return;
    }
    this.location.back();
  }

  onMrdDelete() {
    this.deleteConfirmOpen = true;
  }

  cancelDelete() {
    this.deleteConfirmOpen = false;
  }

  confirmDelete() {
    this.deleteConfirmOpen = false;
    this.api.deleteObject(this.dataObject!._links._self.href).subscribe({
      next: () => {
        if (this.overlayHref) {
          this.overlayDeleted.emit();
          return;
        }
        this.router.navigate([this.tenantCode]);
      },
      error: (error) => this.notificationService.showServerError(error, 'delete.failed', 'WARNING'),
    });
  }
}

